import { useState, useEffect, useCallback } from 'react';
import { documentAPI, PropertyDocument, CreateDocumentData } from '../api/documents';

export const useDocuments = (propertyId?: number) => {
  const [documents, setDocuments] = useState<PropertyDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger les documents d'un bien spécifique
  const loadDocuments = useCallback(async (propId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await documentAPI.getByProperty(propId);
      setDocuments(data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setError('Erreur lors du chargement des documents');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Charger automatiquement si propertyId est fourni
  useEffect(() => {
    if (propertyId) {
      loadDocuments(propertyId);
    }
  }, [propertyId, loadDocuments]);

  // Upload d'un nouveau document
  const uploadDocument = useCallback(async (
    propId: number, 
    file: File, 
    metadata: Omit<CreateDocumentData, 'property_id' | 'original_filename'>
  ) => {
    setError(null);
    try {
      const uploadedDocument = await documentAPI.upload(propId, file, metadata);
      setDocuments(prev => [uploadedDocument.document, ...prev]);
      return uploadedDocument;
    } catch (error) {
      console.error('Failed to upload document:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de l\'upload');
      throw error;
    }
  }, []);

  // Mettre à jour un document
  const updateDocument = useCallback(async (documentId: number, updates: any) => {
    setError(null);
    try {
      const updatedDocument = await documentAPI.update(documentId, updates);
      setDocuments(prev => prev.map(doc => 
        doc.id === documentId ? updatedDocument : doc
      ));
      return true;
    } catch (error) {
      console.error('Failed to update document:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la mise à jour');
      return false;
    }
  }, []);

  // Supprimer un document
  const deleteDocument = useCallback(async (documentId: number) => {
    setError(null);
    try {
      await documentAPI.delete(documentId);
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      return true;
    } catch (error) {
      console.error('Failed to delete document:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la suppression');
      return false;
    }
  }, []);

  // Télécharger un document
  const downloadDocument = useCallback(async (documentId: number, filename: string) => {
    setError(null);
    try {
      const blob = await documentAPI.download(documentId);
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Failed to download document:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors du téléchargement');
      return false;
    }
  }, []);

  // Archiver un document
  const archiveDocument = useCallback(async (documentId: number) => {
    setError(null);
    try {
      await documentAPI.archive(documentId);
      setDocuments(prev => prev.map(doc => 
        doc.id === documentId ? { ...doc, status: 'archived' } : doc
      ));
      return true;
    } catch (error) {
      console.error('Failed to archive document:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de l\'archivage');
      return false;
    }
  }, []);

  return {
    documents,
    isLoading,
    error,
    loadDocuments,
    uploadDocument,
    updateDocument,
    deleteDocument,
    downloadDocument,
    archiveDocument,
  };
};